import { toCamelCase } from '@quickbit/backoffice-core-frontend/src/api/fetchAPI';
import { sessionDataCookieName } from '@quickbit/backoffice-core-frontend/src/context/Session';

// GET only: these endpoints never trigger screening, registration or case updates.
export async function readNeoOffice<T>(path: string): Promise<T> {
  const base =
    process.env.REACT_APP_API_BASE_URL ?? process.env.REACT_APP_BASE_URL ?? '';
  const response = await fetch(`${base}/api/bo/${path}`, {
    credentials: 'include',
    cache: 'no-store',
    signal: AbortSignal.timeout(45000),
  }).catch(() => {
    throw new Error(
      'The lookup did not respond. Check your connection and try again. No risk assessment was returned.'
    );
  });
  if (response.status === 401) {
    document.cookie = `${sessionDataCookieName}=; Max-Age=0; Path=/`;
    window.location.replace(
      window.location.pathname.startsWith('/neo-office/')
        ? '/neo-office/index.html#/login'
        : '/login'
    );
    throw new Error('Your session has expired. Please sign in again.');
  }
  if (!response.ok) {
    throw Object.assign(
      new Error(
        response.status === 403
          ? 'You need AML read access (view_fcu_info or view_neo_office).'
          : response.status === 400
          ? path.startsWith('v1/neo-office/cm1/')
            ? 'Check the archive search filters and page number.'
            : 'Check the search value. IDs must be UUIDs. A Chainalysis customer ID is required when a hash cannot be linked to a single internal customer.'
          : response.status === 404
          ? path.startsWith('v1/neo-office/cm1/')
            ? 'The archive record or document is unavailable in this import.'
            : 'The case does not exist or has been deleted.'
          : response.status === 502 || response.status === 503
          ? 'Chainalysis is unavailable or not configured. Please try again later.'
          : 'Unable to load records. Please try again.'
      ),
      { status: response.status }
    );
  }
  const body: unknown = await response.json();
  return toCamelCase(body as Record<string, unknown>) as T;
}

// Preserve persisted rejection/unknown outcomes so the operator sees the actual
// investigation status rather than a generic error or an accidental retry.
export type ScreeningSubmission = {
  detection_id?: string;
  asset: string;
  hash: string;
  output: string;
  direction: string;
};

export async function registerNeoOfficeScreening<T>(
  body: ScreeningSubmission | Record<string, unknown>,
  endpoint = 'screenings'
): Promise<T> {
  const base =
    process.env.REACT_APP_API_BASE_URL ?? process.env.REACT_APP_BASE_URL ?? '';
  const response = await fetch(`${base}/api/bo/v1/neo-office/${endpoint}`, {
    method: 'POST',
    credentials: 'include',
    signal: AbortSignal.timeout(30000),
    headers: {
      'Content-Type': 'application/json',
      'x-csrftoken': decodeURIComponent(
        document.cookie
          .split('; ')
          .find((cookie) =>
            cookie.startsWith(
              `${process.env.REACT_APP_CSRF_COOKIE_NAME ?? 'csrftoken'}=`
            )
          )
          ?.split('=')
          .slice(1)
          .join('=') ?? ''
      ),
    },
    body: JSON.stringify(body),
  }).catch(() => {
    throw new Error(
      endpoint === 'detect-usdc'
        ? 'Ethereum lookup did not respond. Please try again later. No Chainalysis analysis was started.'
        : 'Registration did not respond. It may have been accepted. Check Recent investigations before trying again.'
    );
  });
  if (response.status === 401) {
    document.cookie = `${sessionDataCookieName}=; Max-Age=0; Path=/`;
    window.location.replace('/neo-office/index.html#/login');
    throw new Error('Your session has expired. Please sign in again.');
  }
  const data: unknown = await response.json().catch(() => null);
  if (response.ok && data && typeof data === 'object') {
    return toCamelCase(data as Record<string, unknown>) as T;
  }
  if (
    data &&
    typeof data === 'object' &&
    'id' in data &&
    'status' in data &&
    (response.ok ||
      (response.status === 502 &&
        ['REJECTED', 'UNKNOWN'].includes(String(data.status))))
  ) {
    return toCamelCase(data as Record<string, unknown>) as T;
  }
  if (
    data &&
    typeof data === 'object' &&
    'error' in data &&
    typeof data.error === 'string'
  ) {
    throw new Error(data.error);
  }
  throw new Error(
    response.status === 400 && endpoint === 'detect-usdc'
      ? 'Paste the complete Ethereum hash: 0x followed by 64 hexadecimal characters.'
      : response.status === 400
      ? 'Unable to start analysis. Check the selected asset/network, complete hash and destination address or output index.'
      : response.status === 403
      ? 'You do not have permission to start this analysis. Sign in again or contact an administrator.'
      : 'Chainalysis could not start the analysis. Check Recent investigations before trying again.'
  );
}
